import { useState } from "react";
import { FloatButton, Popover, Segmented, Typography } from "antd";
import { LayoutOutlined } from "@ant-design/icons";
import { ECRF_ALIGNS, ECRF_VIEWS, useEcrfView, type EcrfAlignId, type EcrfViewId } from "../theme/EcrfViewTheme";

/**
 * Display settings for the eCRF page: page layout and field alignment.
 *
 * A single FloatButton opening a popover, rather than the two stacked
 * FloatButton.Groups this replaces. Those had two problems that share one cause -
 * FloatButton is built for one or two icon actions, not for labelled pickers:
 *
 *   - each group expands ~144px upward on hover, so the lower group grew
 *     through the upper one;
 *   - four of the six Greek labels ("Πλαϊνή στήλη", "Αριστερά", ...) are too
 *     long for a 40px bubble and wrapped or clipped.
 *
 * Segmented rows inside a popover hold the full labels, cannot overlap, and
 * leave room for a third setting later.
 */
export const EcrfViewSwitcher: React.FC = () => {
    const { view, setView, align, setAlign } = useEcrfView();
    const [open, setOpen] = useState(false);

    const content = (
        <div className="ecrf-settings">
            <div className="ecrf-settings-group">
                <Typography.Text type="secondary" className="ecrf-settings-label">
                    Διάταξη σελίδας
                </Typography.Text>
                <Segmented
                    block
                    value={view}
                    onChange={v => setView(v as EcrfViewId)}
                    options={ECRF_VIEWS.map(v => ({ label: v.label, value: v.id }))}
                />
            </div>

            <div className="ecrf-settings-group">
                <Typography.Text type="secondary" className="ecrf-settings-label">
                    Στοίχιση πεδίων
                </Typography.Text>
                <Segmented
                    block
                    value={align}
                    onChange={v => setAlign(v as EcrfAlignId)}
                    options={ECRF_ALIGNS.map(a => ({ label: a.label, value: a.id }))}
                />
            </div>
        </div>
    );

    return (
        <Popover
            content={content}
            trigger="click"
            placement="topRight"
            open={open}
            onOpenChange={setOpen}
            title="Εμφάνιση"
        >
            {/* FloatButton renders its own fixed-position element, so the Popover
                anchors to it directly rather than needing a wrapper. */}
            <FloatButton
                icon={<LayoutOutlined />}
                type={open ? "primary" : "default"}
                tooltip={open ? undefined : "Εμφάνιση"}
                style={{ insetInlineEnd: 24, insetBlockEnd: 24 }}
            />
        </Popover>
    );
};
